import React, { useEffect, useMemo, useState } from 'react';
import {
  Organization,
  ProviderType,
  NewIntegration,
} from 'components/common/ObjectTypes';
import { DraftReferenceState } from 'api/referenceOptions';
import SelectInput from 'components/common/SelectInput';
import { AttentionIcon } from 'components/common/ProjectIcons';
import { useTranslation } from 'react-i18next';
import SelectionPanel from 'components/common/configPanel/SelectionPanel';
import ActionSectionForm from 'components/common/configPanel/ActionSectionForm';
import ScheduleToggle from 'components/common/configPanel/ScheduleToggle';
import {
  DETAILS_SLUG,
  actionForSlug,
  attachRequiredCounts,
  buildActionNav,
  flagSectionErrors,
} from 'components/common/configPanel/buildActionNav';
import { actionDescription } from 'components/common/configPanel/actionSetup';
import { routeSetupActions } from 'components/common/SchemaFormTemplates/actionFilters';
import { getRouteKind } from 'components/common/routeKind';
import { inputClasses } from 'components/common/tailwindClassnames';
import { DraftTestActionButton } from 'components/common/DraftTestActionButton';
import { useHelp, HelpCard } from 'context/HelpContext';
import { providerNeedsBaseUrl } from './providerBaseUrl';
import { isEarthRangerDestinationType } from 'components/destinations/earthRangerUrl';
import EarthRangerSiteInput from 'components/destinations/EarthRangerSiteInput';

type NewRouteStepTwoProps = {
  currentMemberIsAdmin: boolean;
  initialRoute: NewIntegration;
  errors: any;
  rsjfErrors: any;
  selectedProviderType: ProviderType;
  organizations: Organization[];
  loadMoreOrgs: Function;
  orgInputChange: Function;
  onChangeSchemaForm: Function;
  onChangeName: Function;
  onChangeEndpoint: Function;
  onSelectOwner: Function;
  draftReference?: DraftReferenceState;
  authToken?: string;
  // What has been typed so far, so a section that was navigated away from comes
  // back with its values instead of an empty form.
  configurations?: any[];
};

const NewRouteStepTwo = ({
  currentMemberIsAdmin,
  initialRoute,
  errors,
  rsjfErrors,
  selectedProviderType,
  organizations,
  loadMoreOrgs,
  orgInputChange,
  onChangeSchemaForm,
  onChangeName,
  onChangeEndpoint,
  onSelectOwner,
  draftReference,
  authToken,
  configurations = [],
}: NewRouteStepTwoProps) => {
  const { t, i18n } = useTranslation();
  const { setHelpContent } = useHelp();
  // Every section is on the page at once here, so the nav follows the scroll
  // position rather than choosing what renders. This only tracks the last
  // section a nav click asked for.
  const [, setActiveSlug] = useState<string>(DETAILS_SLUG);

  useEffect(() => {
    const kind = getRouteKind(selectedProviderType);
    const cards: HelpCard[] = [
      {
        title: t('Setting up a Route in Gundi'),
        description: t(
          'Find step-by-step instructions for connecting your data source.',
        ),
        button: {
          label: t('Learn More'),
          url: 'https://support.earthranger.com/routes-gundi/create-route',
        },
      },
      ...(selectedProviderType.help_center_url
        ? [
            {
              title: t('{{technology}} Route Set Up', {
                technology: selectedProviderType.name,
              }),
              description: t(
                'Configuration guide specific to {{technology}} Routes.',
                { technology: selectedProviderType.name },
              ),
              button: {
                label: t('Learn More'),
                url: selectedProviderType.help_center_url,
              },
            },
          ]
        : []),
      ...(kind === 'webhook'
        ? [
            {
              title: t('Gundi Webhook API'),
              description: t('Learn how to send data to Gundi using webhooks.'),
              button: {
                label: t('Learn More'),
                url: 'https://support.earthranger.com/developer_docs/custom-webhook',
              },
            },
          ]
        : []),
      ...(kind === 'push-only'
        ? [
            {
              title: t('Gundi API'),
              description: t('Learn how to push data to Gundi using the API.'),
              button: {
                label: t('Learn More'),
                url: 'https://support.earthranger.com/developer_docs/gundi-api',
              },
            },
          ]
        : []),
    ];
    setHelpContent({ cards });
  }, [selectedProviderType, i18n.language, t, setHelpContent]);

  useEffect(() => {
    return () => setHelpContent(null);
  }, [setHelpContent]);

  // Credentials and the pull actions that bring data in (routeSetupActions).
  // Each section carries its own on/off switch, and a switched-on section's
  // required fields block the save — ManageAddRouteStepTwo validates exactly
  // the sections shown here.
  const actions = useMemo(
    () => routeSetupActions(selectedProviderType.actions),
    [selectedProviderType.actions],
  );
  const needsBaseUrl = providerNeedsBaseUrl(selectedProviderType.value);
  const isEarthRanger = isEarthRangerDestinationType(selectedProviderType.name);
  const dataFor = (action: any) =>
    configurations.find((c: any) => c.action === action.id)?.data ?? {};
  // Counts, not "!", on this screen only — see attachRequiredCounts. Applied
  // after flagSectionErrors so a section that both misses required fields and
  // failed a save shows one red chip rather than a chip plus a "!".
  // The details section's own required fields, counted the same way a section's
  // are: name, workspace, and the address for the technologies that need one.
  const detailsRequiredCount = [
    !initialRoute.name?.trim(),
    !initialRoute.owner,
    ...(needsBaseUrl ? [!initialRoute.base_url?.trim()] : []),
  ].filter(Boolean).length;
  const nav = attachRequiredCounts(
    flagSectionErrors(
      buildActionNav({
        actions,
        t,
        detailsLabel: t('Route details'),
        detailsNeedsSetup: Object.keys(errors).length > 0,
        detailsRequiredCount,
      }),
      actions,
      rsjfErrors,
    ),
    actions,
    dataFor,
    rsjfErrors,
  );

  // One action's form, rendered inline with all the others.
  const actionSection = (action: any) =>
    !action ? null : (
      <ActionSectionForm
        target={{
          id: '',
          actions: selectedProviderType.actions || [],
          configurations: [],
        }}
        action={action}
        userIsAdmin={currentMemberIsAdmin}
        formData={dataFor(action)}
        errors={rsjfErrors[action.id]}
        onChange={(data: any) => onChangeSchemaForm(data, action.id)}
        showSetupState={false}
        // The switch is drawn on the section heading's line instead.
        renderScheduleToggle={false}
        // Drawn in the section heading instead, beside the on/off switch.
        showDescription={false}
        // Creation flows have no integration id yet, so ReferenceSelectWidget
        // reaches its live options through draft mode — which it refuses to
        // enter without a token, falling back to a plain text input. Dropping
        // this prop costs the user every reference dropdown on the screen.
        token={authToken}
        draftReference={draftReference}
        headerActions={
          action.schema?.is_executable === true ? (
            <DraftTestActionButton
              action={action}
              providerType={selectedProviderType}
              draftReference={draftReference}
              authToken={authToken}
              cannotTestHint={t(
                'Connections can be tested after the setup workflow is complete.',
              )}
              testid="TestActionHeader-button"
            />
          ) : undefined
        }
      />
    );

  const detailsSection = (
    <div className="rounded border border-ui-field-outline bg-white p-[16px]">
      {/* Label + description on the left, field on the right — same two-column
                rows as the destination and action forms. */}
      <div className="mb-[8px] w-full border-b border-ui-divider-lines pb-[8px] pt-1 transition-all duration-300">
        <div className="flex w-full flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-6">
          <div className="flex flex-col md:w-1/2">
            <label className="mr-1">{t('Route Name')} *</label>
            <span className="mt-1 text-sm text-ui-text-primary-text">
              {t('A descriptive name to identify this Route.')}
            </span>
          </div>
          <div className="md:w-1/2">
            <input
              id="name"
              data-testid="NewRouteStepTwo-name-input"
              placeholder=""
              value={initialRoute.name}
              className={`${inputClasses.standard} w-full`}
              onChange={(e) => onChangeName(e)}
              disabled={false}
            ></input>
            {errors.name && (
              <div className="flex flex-row items-center rounded py-1 text-unlisted-orange-600 transition-colors duration-300">
                <AttentionIcon />
                <p className="ml-1 mt-0.5">{errors.name}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="w-full pb-[8px] pt-1 transition-all duration-300">
        <div className="flex w-full flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-6">
          <div className="flex flex-col md:w-1/2">
            <label className="mr-1">{t('Route Workspace')} *</label>
            <span className="mt-1 text-sm text-ui-text-primary-text">
              {t('The workspace this Route belongs to.')}
            </span>
          </div>
          <div className="md:w-1/2">
            <SelectInput
              options={organizations.map((org: Organization) => ({
                label: org.name,
                value: org.id,
              }))}
              className={'basic-single'}
              autoSelectSingleOption={true}
              isDisabled={false}
              isLoading={false}
              isClearable={false}
              isSearchable={true}
              isMulti={false}
              name={t('Workspace')}
              onChange={(e: any) => onSelectOwner(e)}
              onMenuScrollToBottom={loadMoreOrgs}
              handleInputChange={(e: any) => orgInputChange(e)}
            />
            {errors.owner && (
              <div className="flex flex-row items-center rounded py-1 text-unlisted-orange-600 transition-colors duration-300">
                <AttentionIcon />
                <p className="ml-1 mt-0.5">{errors.owner}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Gundi can't reach these technologies without an address, so the
                connection can't be created without one either. Every other provider
                type has its endpoint baked into the connector. */}
      {needsBaseUrl && (
        <div className="w-full border-t border-ui-divider-lines pt-[8px] transition-all duration-300">
          <div className="flex w-full flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-6">
            <div className="flex flex-col md:w-1/2">
              <label className="mr-1">
                {isEarthRanger ? t('Site URL') : t('URL')} *
              </label>
              <span className="mt-1 text-sm text-ui-text-primary-text">
                {isEarthRanger
                  ? t('The name of the EarthRanger site (e.g. my-site).')
                  : t('The address Gundi uses to reach this system.')}
              </span>
            </div>
            <div className="md:w-1/2">
              {isEarthRanger ? (
                <EarthRangerSiteInput
                  value={initialRoute.base_url || ''}
                  onChange={(e) => onChangeEndpoint(e)}
                  error={errors.base_url}
                />
              ) : (
                <>
                  <input
                    id="base_url"
                    data-testid="NewRouteStepTwo-base-url-input"
                    placeholder=""
                    value={initialRoute.base_url || ''}
                    className={`${inputClasses.standard} w-full`}
                    onChange={(e) => onChangeEndpoint(e)}
                  />
                  {errors.base_url && (
                    <div className="flex flex-row items-center rounded py-1 text-unlisted-orange-600 transition-colors duration-300">
                      <AttentionIcon />
                      <p className="ml-1 mt-0.5">{errors.base_url}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="mt-8 w-full">
      <div className="flex w-full flex-col pb-16">
        {/* form errors */}
        {(Object.keys(errors).length > 0 ||
          Object.keys(rsjfErrors).length > 0) && (
          <div className="mb-6 rounded-sm bg-unlisted-very-light-orange px-3 py-2 text-ui-text-primary-text transition-colors duration-300">
            <AttentionIcon />{' '}
            {t(
              'There are fields that require your attention. Please check and correct the errors.',
            )}
          </div>
        )}
        {/* Same shell as a Route's configuration panel. The Route
                    doesn't exist yet, so everything is committed by one submit in the
                    wizard footer rather than section by section. */}
        <SelectionPanel
          title={t('Configure Data Provider')}
          nav={nav}
          onSelectSection={setActiveSlug}
          stackedSections={nav.flatMap((section) =>
            section.items.map((item) => ({
              slug: item.slug,
              label: item.label,
              description:
                item.slug === DETAILS_SLUG
                  ? undefined
                  : actionDescription(actionForSlug(actions, item.slug)) ||
                    undefined,
              headerRight:
                item.slug === DETAILS_SLUG
                  ? undefined
                  : (() => {
                      const action = actionForSlug(actions, item.slug);
                      return action ? (
                        <ScheduleToggle
                          action={action}
                          data={dataFor(action)}
                          onChange={(data: any) =>
                            onChangeSchemaForm(data, action.id)
                          }
                        />
                      ) : undefined;
                    })(),
              content:
                item.slug === DETAILS_SLUG
                  ? detailsSection
                  : actionSection(actionForSlug(actions, item.slug)),
            })),
          )}
        >
          {null}
        </SelectionPanel>
      </div>
    </div>
  );
};

export default NewRouteStepTwo;
